import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FiArrowLeft, FiCheckCircle, FiList, FiSlash} from "react-icons/fi";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import PageHeader from "../../../../components/common/pageheader/PageHeader";
import styles from "./ExamAttemptDetailPage.module.css";
import Loader from "../../../../components/common/Loader/Loader";
import ConfirmCancelModal from "../../../../components/common/cancelModal/ConfirmCancelModal";

import {
    approveOpenAnswer,
    cancelExamAttempt,
    confirmExamAttempt,
    getExamAttemptAnswerGroups,
    getExamAttemptAnswersByGroup,
    getExamAttemptLogs,
    rejectOpenAnswer
} from "../../../../services/examAttemptsApi";
import {renderAttemptStatusBadge} from "../../../../utils/statusUtil";
import QuestionContent from "../../../../components/common/QuestionContent/QuestionContent";
import QuestionVideo from "../../../../components/common/QuestionVideo/QuestionVideo";
import QuestionExplanation from "../../../../components/common/QuestionExplanation/QuestionExplanation";
import SubjectGroupedAnswers from "./SubjectGroupedAnswers";
import OpenAnswerReview from "../../../../components/common/OpenAnswerReview/OpenAnswerReview";
import {formatDateTime} from "../../../../utils/formatDateTime";

const ExamAttemptDetailPage = () => {
    const {t} = useTranslation();
    const {attemptId} = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const initialStatus = String(
        location?.state?.attemptStatus ||
        location?.state?.myStatus ||
        location?.state?.status ||
        ""
    ).toUpperCase();


    const [logs, setLogs] = useState([]);
    const [answerGroups, setAnswerGroups] = useState([]);
    const [answersRefreshKey, setAnswersRefreshKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(false);
    const [error, setError] = useState("");
    const [attemptStatus, setAttemptStatus] = useState(initialStatus);
    const [showConfirmResultModal, setShowConfirmResultModal] = useState(false);
    const [showCancelResultModal, setShowCancelResultModal] = useState(false);


    useEffect(() => {
        if (!attemptId) return;

        const load = async () => {
            setLoading(true);
            setError("");

            try {
                // Only the collapsed subject/topic headers — each group's answers are fetched when
                // the reader opens that group.
                const [logsRes, answersRes] = await Promise.all([
                    getExamAttemptLogs(attemptId),
                    getExamAttemptAnswerGroups(attemptId),
                ]);

                setLogs(Array.isArray(logsRes) ? logsRes : []);
                setAnswerGroups(Array.isArray(answersRes) ? answersRes : []);

                const statusFromRes =
                    logsRes?.attemptStatus ||
                    logsRes?.myStatus ||
                    answersRes?.attemptStatus ||
                    answersRes?.myStatus ||
                    "";

                if (statusFromRes) setAttemptStatus(String(statusFromRes).toUpperCase());
            } catch (e) {
                const msg = e?.response?.data?.message || t("pages.examAttemptDetail.loadError");
                setError(msg);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [attemptId, t]);

    const normalizedAttemptStatus = String(
        attemptStatus ||
        location?.state?.myStatus ||
        location?.state?.status ||
        ""
    ).toUpperCase();

    const FINAL_STATUSES = ["CONFIRMED", "CANCELLED", "DISQUALIFIED"];

    const canAct = !FINAL_STATUSES.includes(normalizedAttemptStatus);


    const onConfirm = async () => {
        setShowConfirmResultModal(false);
        if (!attemptId) return;
        setActing(true);
        setError("");

        try {
            const res = await confirmExamAttempt(attemptId);
            const next = (res?.attemptStatus || res?.myStatus) ? String(res?.attemptStatus || res?.myStatus).toUpperCase() : "CONFIRMED";
            setAttemptStatus(next);
            toast.success(t("pages.examAttemptDetail.toastConfirmed"));
            navigate(-1);
        } catch (e) {
            const msg = e?.response?.data?.message || t("pages.examAttemptDetail.confirmError");
            setError(msg);
        } finally {
            setActing(false);
        }
    };

    const onCancelAttempt = async (reason) => {
        setShowCancelResultModal(false);
        if (!attemptId) return;
        setActing(true);
        setError("");

        try {
            const res = await cancelExamAttempt(attemptId, reason);
            const next = (res?.attemptStatus || res?.myStatus) ? String(res?.attemptStatus || res?.myStatus).toUpperCase() : "CANCELLED";
            setAttemptStatus(next);
            toast.success(t("pages.examAttemptDetail.toastCancelled"));
            navigate(-1);
        } catch (e) {
            const msg = e?.response?.data?.message || t("pages.examAttemptDetail.cancelError");
            setError(msg);
        } finally {
            setActing(false);
        }
    };

    // Re-grading changes both the group scores and the card that was graded, so refresh the headers
    // and bump the token that reloads whichever groups are currently open.
    const reloadAnswers = async () => {
        try {
            const groupsRes = await getExamAttemptAnswerGroups(attemptId);
            setAnswerGroups(Array.isArray(groupsRes) ? groupsRes : []);
            setAnswersRefreshKey((k) => k + 1);
        } catch {
            // keep what is on screen on a refresh error
        }
    };

    // Teacher override of an OPEN answer's correctness; the backend recomputes the attempt score.
    const handleApproveOpen = async (answerId) => {
        if (acting) return;
        setActing(true);
        setError("");
        try {
            await approveOpenAnswer(attemptId, answerId);
            toast.success(t("pages.examAttemptDetail.openApproved"));
            await reloadAnswers();
        } catch (e) {
            toast.error(e?.response?.data?.message || t("pages.examAttemptDetail.openGradeError"));
        } finally {
            setActing(false);
        }
    };

    const handleRejectOpen = async (answerId) => {
        if (acting) return;
        setActing(true);
        setError("");
        try {
            await rejectOpenAnswer(attemptId, answerId);
            toast.success(t("pages.examAttemptDetail.openRejected"));
            await reloadAnswers();
        } catch (e) {
            toast.error(e?.response?.data?.message || t("pages.examAttemptDetail.openGradeError"));
        } finally {
            setActing(false);
        }
    };


    return (
        <div className={styles.container}>
            <PageHeader
                title={t("pages.examAttemptDetail.logsTitle")}
                icon={<FiList/>}
                description={t("pages.examAttemptDetail.logsSubtitle")}
            />

            <div className={styles.headerRow}>
                <button
                    type="button"
                    className={styles.backBtn}
                    onClick={() => navigate(-1)}
                >
                    <FiArrowLeft/>
                    <span>{t("common.back")}</span>
                </button>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {loading ? (
                <Loader fullscreen/>
            ) : (
                <>
                    <div>
                        <div className={styles.card}>
                            <div className={styles.cardTitle}>{t("pages.examAttemptDetail.resultInfoTitle")}</div>
                            <div className={styles.logsList}>
                                <div className={styles.logRow}>
                                    <div className={styles.resultInfoItems}>
                                        <div className={styles.resultInfo}>
                                            <div>
                                                {t("common.status")}: {renderAttemptStatusBadge(attemptStatus)}
                                            </div>
                                            <div>
                                                {t("pages.examAttemptDetail.finalResult")}: {renderAttemptStatusBadge(location?.state?.finalStatus)}
                                            </div>
                                            <div style={{marginTop: '2px'}}>
                                                {t("pages.examAttempts.colScore")}: {location?.state?.score}
                                            </div>
                                            <div style={{marginTop: '6px'}}>
                                                {t("pages.examAttempts.colCorrectTotal")}: {location?.state?.result}
                                            </div>
                                        </div>
                                        {
                                            location?.state?.cancelResultReason &&
                                            <div className={styles.resultCancelInfo}>
                                                {t("pages.examAttemptDetail.cancelReason")}:
                                                <div className={styles.resultCancelReason}>
                                                    {location?.state?.cancelResultReason}
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.sections}>
                        <div className={styles.card}>
                            <div className={styles.cardTitle}>{t("pages.examAttemptDetail.logs")}</div>

                            {logs.length === 0 ? (
                                <div className={styles.infoText}>{t("pages.examAttemptDetail.noLogs")}</div>
                            ) : (
                                <div className={styles.logsList}>
                                    {logs.map((l) => (
                                        <div key={l.id} className={styles.logRow}>
                                            <div className={styles.logTop}>
                                                <div className={styles.logType}>
                                                    {l.description || "-"}
                                                </div>
                                                <div
                                                    className={
                                                        l.suspicious
                                                            ? styles.badgeSuspicious
                                                            : styles.badgeNormal
                                                    }
                                                >
                                                    {l.suspicious ? t("pages.examAttemptDetail.suspicious") : t("pages.examAttemptDetail.normal")}
                                                </div>
                                            </div>

                                            <div className={styles.logBottom}>
                                                <div className={styles.logTime}>
                                                    {formatDateTime(l.timestamp || "-")}
                                                </div>
                                                {l.imageUrl ? (
                                                    <a
                                                        className={styles.logImageLink}
                                                        href={l.imageUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <img
                                                            src={l.imageUrl}
                                                            alt="log"
                                                            className={styles.logThumb}
                                                            loading="lazy"
                                                        />
                                                    </a>
                                                ) : (
                                                    <span className={styles.logLinkDisabled}>
                                                      {t("pages.examAttemptDetail.noImage")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className={styles.card}>
                            <div className={styles.cardTitle}>{t("pages.examAttemptDetail.questions")}</div>

                            {answerGroups.length === 0 ? (
                                <div className={styles.infoText}>{t("pages.examAttemptDetail.noAnswers")}</div>
                            ) : (
                                <div className={styles.answersList}>
                                    <SubjectGroupedAnswers
                                        groups={answerGroups}
                                        refreshKey={answersRefreshKey}
                                        loadGroup={(group) => getExamAttemptAnswersByGroup(attemptId, {
                                            subjectId: group.subjectId,
                                            topicId: group.topicId,
                                        })}
                                        renderQuestion={(item, number) => {
                                        const q = item?.question;
                                        const qKey = q?.id ?? item?.id;

                                        return (
                                            <div key={qKey} className={q?.studentAnswered ? styles.answerBlock : styles.unansweredBlock}>
                                                <div className={styles.qText}>
                                                    <span className={styles.qNumber}>{number}.</span>
                                                    <QuestionContent text={q?.text || t("pages.examAttemptDetail.questionFallback")} />
                                                </div>

                                                {q?.attachmentUrl && (
                                                    <img src={q.attachmentUrl} alt="question" className={styles.imagePreview}/>
                                                )}

                                                <QuestionVideo playbackUrl={q?.videoPlaybackUrl} thumbnailUrl={q?.videoThumbnailUrl}/>

                                                <QuestionExplanation explanation={q?.explanation}/>

                                                {q?.type === "OPEN" ? (
                                                    <OpenAnswerReview
                                                        question={q}
                                                        canGrade={canAct}
                                                        acting={acting}
                                                        onApprove={() => handleApproveOpen(item.id)}
                                                        onReject={() => handleRejectOpen(item.id)}
                                                    />
                                                ) : (
                                                    <div className={styles.options}>
                                                        {(q?.options || []).map((opt, optIndex) => (
                                                            <div
                                                                key={opt.id}
                                                                className={[
                                                                    styles.optionRow,
                                                                    opt.correct && styles.correctOption,
                                                                    opt.studentSelected &&
                                                                    !opt.correct &&
                                                                    styles.wrongOption,
                                                                ]
                                                                    .filter(Boolean)
                                                                    .join(" ")}
                                                            >
                                                                <QuestionContent text={opt.optionText} />
                                                                {opt.attachmentUrl && (
                                                                    <img
                                                                        src={opt.attachmentUrl}
                                                                        alt="option"
                                                                        className={styles.imagePreview}
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }} />
                                </div>
                            )}
                        </div>
                    </div>

                    {canAct && (
                        <div className={styles.actions}>
                            <button
                                type="button"
                                className={styles.confirmBtn}
                                onClick={() => setShowConfirmResultModal(true)}
                                disabled={acting}
                            >
                                <FiCheckCircle/>
                                <span>{acting ? "..." : t("pages.examAttemptDetail.confirmResult")}</span>
                            </button>

                            <button
                                type="button"
                                className={styles.cancelBtn}
                                onClick={() => setShowCancelResultModal(true)}
                                disabled={acting}
                            >
                                <FiSlash/>
                                <span>{acting ? "..." : t("pages.examAttemptDetail.cancelResult")}</span>
                            </button>
                        </div>
                    )}

                    <ConfirmCancelModal
                        isOpen={showConfirmResultModal}
                        message={t("pages.examAttemptDetail.confirmResultMessage")}
                        confirmText={t("common.confirm")}
                        onConfirm={onConfirm}
                        onCancel={() => setShowConfirmResultModal(false)}
                    />

                    <ConfirmCancelModal
                        isOpen={showCancelResultModal}
                        message={t("pages.examAttemptDetail.cancelResultMessage")}
                        onConfirm={onCancelAttempt}
                        onCancel={() => setShowCancelResultModal(false)}
                        requireReason={true}
                    />
                </>
            )}
        </div>
    );

};

export default ExamAttemptDetailPage;
